import {
  AloftDate,
  MarketConditionsAdjustments,
  PairedCostResults,
  PairedCostVersion,
  Tkv2DepreciationSettings,
  Tkv2PairedCostSettings,
  Tkv2Property,
  getPairedCostResults,
  noop,
} from '@aloft/toolkit-shared';

import { useCallback, useMemo, useState } from 'react';

import { useGetTkv2OrderDepreciationSettingsAPI } from '../../hooks/api/schema-hooks/useGetTkv2OrderDepreciationSettingsAPI.ts';
import { useGetTkv2OrderPairedCostSettingsAPI } from '../../hooks/api/schema-hooks/useGetTkv2OrderPairedCostSettingsAPI.ts';
import { usePutTkv2OrderDepreciationSettingsAPI } from '../../hooks/api/schema-hooks/usePutTkv2OrderDepreciationSettingsAPI.ts';
import { usePutTkv2OrderPairedCostSettingsAPI } from '../../hooks/api/schema-hooks/usePutTkv2OrderPairedCostSettingsAPI.ts';

type Params = {
  orderID: string;
  subject?: Tkv2Property;
  comps?: Record<string, Tkv2Property>;
  compsMC?: Record<string, MarketConditionsAdjustments>;
  compSeq?: string[];
  effectiveDate?: AloftDate;
};

type Hook = {
  depreciationSettings: Tkv2DepreciationSettings;
  saveDepreciationSettings: (
    next: Tkv2DepreciationSettings,
    saveAsDefault: boolean,
  ) => Promise<void>;

  pairedCostResults: PairedCostResults;
  pairedCostSettings: Tkv2PairedCostSettings;
  isPairedCostSettingsLoading: boolean;
  savePairedCostSettings: (next: Tkv2PairedCostSettings, saveAsDefault: boolean) => Promise<void>;
  pairedCostVersion: PairedCostVersion;
  setPairedCostVersion: (version: PairedCostVersion) => void;
};

export const useNewTkv2PairedCostContext = (params: Params): Hook | undefined => {
  const { orderID, subject, comps, compsMC, compSeq, effectiveDate } = params;
  const [pairedCostVersion, setPairedCostVersion] = useState<PairedCostVersion>('legacy');

  const { refetch: refetchDepreciationSettings, data: depreciationSettings } =
    useGetTkv2OrderDepreciationSettingsAPI({ pathParams: { orderID } });

  const { mutateAsync: mutateDepreciationSettingsAsync } = usePutTkv2OrderDepreciationSettingsAPI({
    onSuccess: () => refetchDepreciationSettings(),
  });

  const saveDepreciationSettings = useCallback(
    (next: Tkv2DepreciationSettings, saveAsDefault: boolean): Promise<void> =>
      mutateDepreciationSettingsAsync({
        pathParams: { orderID },
        queryParams: { saveAsDefault: `${saveAsDefault}` },
        body: next,
      }).then(noop),
    [orderID, mutateDepreciationSettingsAsync],
  );

  const {
    refetch: refetchPairedCostSettings,
    data: pairedCostSettings,
    isLoading: isPairedCostSettingsLoading,
  } = useGetTkv2OrderPairedCostSettingsAPI({ pathParams: { orderID } });
  const { mutateAsync: mutateAsyncPairedCostSettings } = usePutTkv2OrderPairedCostSettingsAPI({
    onSuccess: () => refetchPairedCostSettings(),
  });

  const pairedCostResults = useMemo(() => {
    if (
      !subject ||
      !comps ||
      !compsMC ||
      !compSeq ||
      !effectiveDate ||
      !depreciationSettings ||
      !pairedCostSettings
    ) {
      return undefined;
    }
    return getPairedCostResults({
      subject,
      comps,
      compsMC,
      compSeq,
      effectiveDate,
      depreciationSettings,
      pairedCostSettings,
      version: pairedCostVersion,
    });
  }, [
    subject,
    comps,
    compsMC,
    compSeq,
    effectiveDate,
    depreciationSettings,
    pairedCostSettings,
    pairedCostVersion,
  ]);

  const savePairedCostSettings = useCallback(
    (next: Tkv2PairedCostSettings, saveAsDefault: boolean): Promise<void> =>
      mutateAsyncPairedCostSettings({
        pathParams: { orderID },
        queryParams: { saveAsDefault: `${saveAsDefault}` },
        body: next,
      }).then(noop),
    [orderID, mutateAsyncPairedCostSettings],
  );

  if (
    depreciationSettings === undefined ||
    pairedCostResults === undefined ||
    pairedCostSettings === undefined
  ) {
    return undefined;
  }

  return {
    depreciationSettings,
    saveDepreciationSettings,

    pairedCostResults,
    pairedCostSettings,
    isPairedCostSettingsLoading,
    savePairedCostSettings,
    pairedCostVersion,
    setPairedCostVersion,
  };
};
